import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import properties from "../../apiConfigs/properties";
import { ReactComponent as SearchSVG } from "../../assets/icons/search.svg";
import { ReactComponent as View } from "../../assets/view.svg";
import Pagination from "../../components/Pagination";
import Input from "../../designComponents/Input";
import Table from "../../designComponents/Table";
import {
  renderFullDetails,
  renderstatus,
} from "../../designComponents/Table/renderMethods";
import { SCRAPPED_ROUTE } from "../../routes/routePaths";
import { humanReadableDate } from "../../utils/date";
import { debounce } from "../../utils/debounce";
import requestAPI from "../../utils/requestAPI";
import { SearchInputWrapperStyled } from "../Users/style";
import {
  AcceptButton,
  ButtonsSection,
  RejectButton,
  Title,
  Wrapper,
} from "./Details/styles";
import Button from "../../designComponents/Button";
import ReusableModalButton from "../../designComponents/FilterButton";
import LabelInputField from "../../designComponents/LabelInputField/LabelInputField";
import Dropdown from "../../designComponents/Dropdown/Dropdown";
import Datepicker from "../../designComponents/Datepicker";
type PropertyType = {
  _id: string;
  address: string;
  title: string;
  price: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  landlord: {
    email: string;
    firstName: string;
    lastName: string;
    avatar: string;
  };
  pricing: {
    price: string;
  };
};

type Props = {};
interface PropertySearchParams {
  page: number;
  limit: number;
  search: string;
  status: string;
  totalPages: number;
  total: number;
  bedrooms: string | null;
  bathrooms: string | null;
  from: Date | "";
  to: Date | "";
  priceMin: string;
  priceMax: string;
  BER: number;
}
const Properties = (props: Props) => {
  const navigate = useNavigate();
  const [changeCount, setChangeCount] = useState(0);

  const [loading, setLoading] = useState<boolean>(false);
  const [propertiesData, setPropertiesData] = useState<PropertyType[]>([]);
  const location = useLocation();
  const paramsi = new URLSearchParams(location.search);
  const [cardKey, setCardKey] = useState<string>("all");
  const [paginationParams, setPaginationParams] =
    useState<PropertySearchParams>({
      page: 1,
      limit: 10,
      search: "",
      status: "",
      totalPages: 1,
      total: 0,
      bedrooms: "",
      bathrooms: "",
      from: "",
      to: "",
      priceMin: "",
      priceMax: "",
      BER: 0,
    });

  const getAllProperties = async (params: any) => {
    setLoading(true);
    try {
      const query = {
        page: params.page,
        limit: params.limit,
        search: params.search,
        bedrooms: params.bedrooms === 0 ? null : params.bedrooms,
        bathrooms: params.bathrooms === 0 ? null : params.bathrooms,
        price:
          params.priceMax && params.priceMin
            ? `${params.priceMin},${params.priceMax}`
            : null,
        from: params.from,
        to: params.to,
        status: paramsi.get("status") ?? params.status,
      };

      const response = await requestAPI(properties.getProperties(query));
      setPropertiesData(response.properties);
      setPaginationParams((prevParams) => ({
        ...prevParams,
        totalPages: response.pagination.totalPages,
        total: response.pagination.total,
      }));
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const delayedFetch = useCallback(debounce(getAllProperties, 400), [cardKey]);

  useEffect(() => {
    delayedFetch(paginationParams);
  }, [delayedFetch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPaginationParams((prevParams) => {
      const updatedParams = { ...prevParams, search: value, page: 1 };
      delayedFetch(updatedParams);
      return updatedParams;
    });
  };

  const handleCurrentPage = (page: number) => {
    delayedFetch({ ...paginationParams, page });
    setPaginationParams((prevParams) => ({ ...prevParams, page }));
  };

  const handleLimitChange = (limit: number) => {
    delayedFetch({ ...paginationParams, limit, page: 1 });
    setPaginationParams((prevParams) => ({ ...prevParams, limit, page: 1 }));
  };

  const tableData = {
    headings: [
      { key: "sn", label: "SN" },
      { key: "title", label: "Title" },
      { key: "address", label: "Address" },
      { key: "landlord", label: "Landlord" },
      { key: "requestedDate", label: "Date Created" },
      { key: "statuss", label: "Status" },
      { key: "actions", label: "Actions" },
    ],
    body: Array.isArray(propertiesData)
      ? propertiesData.map((property, index) => ({
          id: property._id,
          landlord: renderFullDetails({
            avatar: property?.landlord?.avatar,
            email: property?.landlord?.email,
            firstName: property?.landlord?.firstName,
            lastName: property?.landlord?.lastName,
          }),
          title: property?.title ?? "No title was Provided",
          sn: `${index + 1}.`,
          address: property?.address,
          requestedDate: humanReadableDate(property?.createdAt),
          statuss: renderstatus(property?.status),
          actions: (
            <div style={{ alignItems: "center", justifyContent: "center" }}>
              <View fill="#7E7E7E"></View>
            </div>
          ),
        }))
      : [],
  };
  useEffect(() => {
    const fieldsToTrack = [
      "bedrooms",
      "from",
      "to",
      "status",
      "priceMin",
      "priceMax",
    ];
    let changes = 0;

    fieldsToTrack.forEach((field) => {
      //@ts-ignore
      if (paginationParams[field] !== "") {
        changes++;
      }
    });

    setChangeCount(changes);
  }, [paginationParams]);
  return (
    <Wrapper>
      <ButtonsSection>
        <Title>Property Management</Title>
        <Button
          onClick={() => {
            navigate(SCRAPPED_ROUTE);
          }}
        >
          Scrap property
        </Button>
      </ButtonsSection>

      <ButtonsSection>
        <SearchInputWrapperStyled style={{ width: 367 }}>
          <Input
            value={paginationParams.search}
            name="properties"
            onChange={handleInputChange}
            placeholder="Search Properties"
            iconLeft={<SearchSVG />}
          />
        </SearchInputWrapperStyled>
        <ButtonsSection>
          <ReusableModalButton icon changeCount={changeCount}>
            <ButtonsSection>
              <LabelInputField
                onChange={(e) => {
                  setPaginationParams({
                    ...paginationParams,
                    bedrooms: e.target.value,
                  });
                }}
                value={`${paginationParams.bedrooms}`}
                name="text"
                label="No of bedrooms"
                style={{
                  label: {
                    fontSize: "14px",
                    fontWeight: "500px",
                    color: "#555A77",
                  },
                  input: {
                    width: "100%",
                    height: 50,
                    border: "1px solid #F1EFFB",
                    padding: 8,
                    borderRadius: 6,
                    backgroundColor: "#F1EFFB",
                  },
                  container: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    paddingTop: 14,
                    width: "100%",
                  },
                }}
              ></LabelInputField>
              <LabelInputField
                onChange={(e) => {
                  setPaginationParams({
                    ...paginationParams,
                    bathrooms: e.target.value,
                  });
                }}
                value={`${paginationParams.bathrooms}`}
                name="Text"
                label="No of Bathrooms"
                style={{
                  label: {
                    fontSize: "14px",
                    fontWeight: "500px",
                    color: "#555A77",
                  },
                  input: {
                    width: "100%",
                    height: 50,
                    border: "1px solid #F1EFFB",
                    padding: 8,
                    borderRadius: 6,
                    backgroundColor: "#F1EFFB",
                  },
                  container: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    paddingTop: 14,
                    width: "100%",
                  },
                }}
              ></LabelInputField>
            </ButtonsSection>
            <ButtonsSection>
              <LabelInputField
                onChange={(e) => {
                  setPaginationParams({
                    ...paginationParams,
                    priceMin: e.target.value,
                  });
                }}
                value={paginationParams.priceMin}
                name="Text"
                label="Price From"
                style={{
                  label: {
                    fontSize: "14px",
                    fontWeight: "500px",
                    color: "#555A77",
                  },
                  input: {
                    width: "100%",
                    height: 50,
                    border: "1px solid #F1EFFB",
                    padding: 8,
                    borderRadius: 6,
                    backgroundColor: "#F1EFFB",
                  },
                  container: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    paddingTop: 14,
                    width: "100%",
                  },
                }}
              ></LabelInputField>
              <LabelInputField
                onChange={(e) => {
                  setPaginationParams({
                    ...paginationParams,
                    priceMax: e.target.value,
                  });
                }}
                value={paginationParams.priceMax}
                name="Text"
                label="Price To"
                style={{
                  label: {
                    fontSize: "14px",
                    fontWeight: "500px",
                    color: "#555A77",
                  },
                  input: {
                    width: "100%",
                    height: 50,
                    border: "1px solid #F1EFFB",
                    padding: 8,
                    borderRadius: 6,
                    backgroundColor: "#F1EFFB",
                  },
                  container: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    paddingTop: 14,
                    width: "100%",
                  },
                }}
              ></LabelInputField>
            </ButtonsSection>
            <Dropdown
              options={[
                { label: "All", value: "" },
                { label: "Active", value: "ACTIVE" },
                { label: "Accepted", value: "ACCEPTED" },
                { label: "Under Review", value: "UNDER_REVIEW" },
                { label: "Draft", value: "DRAFT" },
                { label: "Rejected", value: "REJECTED" },
              ]}
              label="Select Status"
              style={{
                container: { paddingTop: 10 },
                label: { paddingBottom: 6, color: "#555A77" },
                select: {
                  backgroundColor: "#F1EFFB",
                  fontSize: 14,
                  fontFamily: "urbanist",
                  fontWeight: 500,
                  padding: "8px",
                },
              }}
              onChange={(e) => {
                setPaginationParams({
                  ...paginationParams,
                  status: e.target.value,
                });
              }}
            ></Dropdown>
            <ButtonsSection>
              <Datepicker
                selectedDate={paginationParams.from}
                label="Date Filter"
                name="from"
                onChange={(date) => {
                  if (date) {
                    setPaginationParams({
                      ...paginationParams,
                      from: date,
                    });
                  }
                }}
                style={{
                  label: {
                    fontSize: "14px",
                    fontWeight: "500px",
                    color: "#555A77",
                  },
                  input: {
                    height: 50,
                    border: "1px solid #F1EFFB",
                    padding: 8,
                    borderRadius: 6,
                    width: "100%", // This makes the input take 100% of its container
                  },
                  container: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    paddingTop: 14,
                    width: "50%", // This makes the container take up 50% of the parent's width
                  },
                }}
              />

              <Datepicker
                selectedDate={paginationParams.to}
                label=" "
                name="to"
                onChange={(date) => {
                  if (date) {
                    setPaginationParams({
                      ...paginationParams,
                      to: date,
                    });
                  }
                }}
                style={{
                  label: {
                    fontSize: "14px",
                    fontWeight: "500px",
                    color: "#555A77",
                  },
                  input: {
                    height: 50,
                    border: "1px solid #F1EFFB",
                    padding: 8,
                    borderRadius: 6,
                    width: "100%", // This makes the input take 100% of its container
                  },
                  container: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    paddingTop: 34,
                    width: "50%", // This makes the container take up 50% of the parent's width
                  },
                }}
              />
            </ButtonsSection>
            <ButtonsSection style={{ paddingTop: 20 }}>
              <AcceptButton
                onClick={() => {
                  delayedFetch(paginationParams);
                }}
              >
                Apply
              </AcceptButton>
              <RejectButton
                onClick={() => {
                  delayedFetch({
                    page: 1,
                    limit: 10,
                    search: "",
                    totalPages: 1,
                    total: 0,
                    bedrooms: null,
                    bathrooms: null,
                    price: 0,
                    priceMin: "",
                    priceMax: "",
                    from: null,
                    to: null,
                    BER: 0,
                    status: "",
                  });
                  setPaginationParams({
                    page: 1,
                    limit: 10,
                    search: "",
                    status: "",
                    totalPages: 1,
                    total: 0,
                    bedrooms: "",
                    bathrooms: "",
                    from: "",
                    to: "",
                    priceMin: "",
                    priceMax: "",
                    BER: 0,
                  });
                }}
              >
                Reset
              </RejectButton>
            </ButtonsSection>
          </ReusableModalButton>
        </ButtonsSection>
      </ButtonsSection>

      <Table
        tableData={tableData}
        isLoading={loading}
        rowClick={(id) => {
          navigate(`${id}`);
        }}
        noData="No data to display."
        headingBgColor="#fdfdf9"
        showMenuIcon={false}
        evenRowBgColor="#fff"
      />
      <Pagination
        activePage={paginationParams.page}
        totalPages={paginationParams.totalPages}
        limit={paginationParams.limit}
        onCurrentPageChange={handleCurrentPage}
        onLimitChange={handleLimitChange}
      />
    </Wrapper>
  );
};

export default Properties;
